import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import * as sdkTypes from '@vansite/ts-sharetribe-flex-sdk/types';
import Decimal from 'decimal.js';
import React, { memo, useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import config from '../../../config';

import BookingDatesForm from '../../../forms/BookingDatesForm/BookingDatesForm';
import { formatMoney } from '../../../util/currency';

const { Money } = sdkTypes;

const closedListingButtonSx = (theme) => ({
  borderLeft: `1px solid ${theme.tokens.matterColorNegative}`,
  width: '100%',
  padding: '15px 24px',
  textAlign: 'center',
});

const lineItemsTotal = (lineItems) => {
  const amount = lineItems.reduce((total, item) => {
    return total + item.lineTotal.amount;
  }, 0);
  const currency = lineItems[0] ? lineItems[0].lineTotal.currency : config.currency;
  return new Money(new Decimal(amount).round().toNumber(), currency);
};

const DesktopBookingPanelComponent = ({
  bookingLimit,
  appliedVoucher = null,
  autoOpenModal = false,
  fetchLineItemsInProgress,
  fetchTimeSlotsInProgress,
  initialDates = null,
  isClosed,
  isOwnListing = false,
  lineItems = [],
  currentListing,
  minimumNights = 1,
  onFetchTimeSlots,
  onFetchTransactionLineItems,
  onSubmit,
  showBookingDatesForm,
  timeSlots,
  distanceToLocation,
  showAnnualPassError,
  voucherValidationError = null,
  voucherValidationInProgress = false,
}) => {
  const intl = useIntl();
  const [hasDatesSelected, setHasDatesSelected] = useState(
    Boolean(initialDates && initialDates[0] && initialDates[1]),
  );
  const [voucherCode, setVoucherCode] = useState(null);
  const lastBookingDataRef = useRef({ bookingDates: [null, null], guestCount: {} });

  // Calculate total from line items
  const filteredLineItems = lineItems.filter(item => item.includeFor.includes('customer'));
  const totalPrice = lineItems.length > 0 ? lineItemsTotal(filteredLineItems) : null;
  const nightLineItem = lineItems.find(item => item.code === 'line-item/night');
  const nightCount = nightLineItem?.quantity || 0;

  const fetchLineItems = useCallback(({
    bookingDates,
    guestCount,
    voucher = null,
    extraLineItems = {},
    customLineItems = {},
  }) => {
    const [startDate, endDate] = bookingDates;
    const datesSelected = Boolean(startDate && endDate);

    if (datesSelected && onFetchTransactionLineItems) {
      onFetchTransactionLineItems({
        bookingData: {
          startDate: startDate.utc().toISOString(),
          endDate: endDate.utc().toISOString(),
          guestCount: {
            adultCount: guestCount.adult,
            childCount: guestCount.child,
            toddlerCount: guestCount.toddler,
            petCount: guestCount.pet,
          },
          ...(voucher ? { voucher } : {}),
          extraLineItems,
          customLineItems,
        },
        listingId: currentListing.id,
        isOwnListing,
      });
    }
  }, [onFetchTransactionLineItems, currentListing.id, isOwnListing]);

  const handleBookingDataChange = useCallback(({ bookingDates, guestCount, extraLineItems, customLineItems }) => {
    const [startDate, endDate] = bookingDates;
    const datesSelected = Boolean(startDate && endDate);
    setHasDatesSelected(datesSelected);

    // Store latest booking data for voucher re-fetch — must include extras and
    // custom items, otherwise applying a voucher recomputes against night + base
    // commission only, capping gift cards / discounts to that reduced subtotal.
    lastBookingDataRef.current = { bookingDates, guestCount, extraLineItems, customLineItems };

    // Include voucher if one is set
    const voucher = voucherCode ? { code: voucherCode } : null;
    fetchLineItems({ bookingDates, guestCount, voucher, extraLineItems, customLineItems });
  }, [fetchLineItems, voucherCode]);

  const handleApplyVoucher = useCallback((code) => {
    setVoucherCode(code);
    const { bookingDates, guestCount, extraLineItems, customLineItems } = lastBookingDataRef.current;
    const [startDate, endDate] = bookingDates;

    // Only fetch if dates are selected
    if (startDate && endDate) {
      fetchLineItems({ bookingDates, guestCount, voucher: { code }, extraLineItems, customLineItems });
    }
  }, [fetchLineItems]);

  return (
    <Box display="flex" flexDirection="column" width={1}>
      {!hasDatesSelected ? (
        <Typography color="text.secondary" mb={2} variant="body1">
          <FormattedMessage id="BookingPanel.selectDatesToShowPrices" />
        </Typography>
      ) : fetchLineItemsInProgress ? (
        <Skeleton height={32} sx={{ mb: 2 }} variant="text" width={180} />
      ) : totalPrice ? (
        <Box alignItems="baseline" display="flex" gap={1} mb={2}>
          <Typography component="span" variant="h5">
            {formatMoney(intl, totalPrice)}
          </Typography>
          {nightCount > 0 ? (
            <Typography color="text.secondary" component="span" variant="body2">
              <FormattedMessage id="BookingPanel.forNightCount" values={{ count: nightCount }} />
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {showAnnualPassError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {showAnnualPassError}
        </Alert>
      ) : null}

      {showBookingDatesForm ? (
        <BookingDatesForm
          appliedVoucher={appliedVoucher}
          autoOpenModal={autoOpenModal}
          bookingLimit={bookingLimit}
          currentListing={currentListing}
          distanceToLocation={distanceToLocation}
          fetchLineItemsInProgress={fetchLineItemsInProgress}
          fetchTimeSlotsInProgress={fetchTimeSlotsInProgress}
          initialDates={initialDates}
          lineItems={lineItems}
          minimumNights={minimumNights}
          onApplyVoucher={handleApplyVoucher}
          onBookingDataChange={handleBookingDataChange}
          onFetchTimeSlots={onFetchTimeSlots}
          onSubmit={onSubmit}
          showAnnualPassError={showAnnualPassError}
          timeSlots={timeSlots}
          voucherValidationError={voucherValidationError}
          voucherValidationInProgress={voucherValidationInProgress}
        />
      ) : isClosed ? (
        <Box component="div" sx={closedListingButtonSx}>
          <FormattedMessage id="BookingPanel.closedListingButtonText" />
        </Box>
      ) : null}
    </Box>
  );
};

DesktopBookingPanelComponent.displayName = 'DesktopBookingPanel';

const DesktopBookingPanel = memo(DesktopBookingPanelComponent);

export default DesktopBookingPanel;
